import { useState, useEffect, useRef } from "react";
import { ScanLine, Sparkles, Loader2, ImagePlus, X, CheckCircle2, FileText, AlertCircle, FileSignature, History } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { runDocumentsScanJob } from "../../lib/scanJobs";
import { Button } from "../ui/button";
import Zb1Watermark from "./Zb1Watermark";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

/**
 * Phase 4.20 + Feb-2026 Multi-Doc — ZB1 / COC / Service-Heft Scan-Card.
 *
 * Lädt ein bis sechs Fahrzeug-Dokumente (Fotos / PDFs) hoch und schickt sie
 * an POST /api/listings/scan-documents. Gemini Vision klassifiziert jedes
 * Dokument (ZB1 / COC / Service-Heft) und extrahiert die jeweils passenden
 * Felder. Der Merge wird als ein `fields`-Objekt an `onResult` durchgereicht,
 * sodass die Aufruf-Seite (CreateListingPage) wie bisher arbeiten kann.
 */
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 6;

// Jul-2026 (Performance) — Bilder vor dem Upload auf max. 2200px / JPEG 85 %
// verkleinern. PDFs & kleine Dateien bleiben unverändert.
async function compressForScan(file) {
    if (!file.type.startsWith("image/") || file.size < 900_000) return file;
    try {
        const bitmap = await createImageBitmap(file);
        const maxSide = 2200;
        const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
        if (blob && blob.size < file.size) {
            return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
        }
        return file;
    } catch { return file; }
}

const FIELD_LABELS = {
    hsn: "HSN (2.1)",
    tsn: "TSN (2.2)",
    vin: "Fahrgestellnr. (E)",
    brand: "Marke (D.1)",
    model: "Modell (D.3)",
    manufacturer: "Hersteller (2)",
    vehicle_class: "Fahrzeugklasse (J)",
    displacement: "Hubraum P.1 (cm³)",
    power_kw: "Nennleistung P.2 (kW)",
    power_rpm: "Drehzahl P.2 (U/min)",
    fuel: "Kraftstoff (P.3)",
    max_speed_kmh: "Höchstgeschw. T (km/h)",
    emission_class: "Schadstoffkl. (14)",
    seat_count: "Sitze (S.1)",
    length_mm: "Länge (mm)",
    width_mm: "Breite (mm)",
    height_mm: "Höhe (mm)",
    weight_kg: "Leergewicht G (kg)",
    max_weight_kg: "zul. Gesamtg. F.1 (kg)",
    first_registration_date: "Erstzulassung (D/12)",
    license_plate: "Kennzeichen (A)",
    bemerkungen: "Bemerkungen (22)",
    previous_owner_first_name: "Vorbesitzer Vorname (C.1.2)",
    previous_owner_last_name_or_company: "Vorbesitzer Name/Firma (C.1.1)",
    previous_owner_street: "Vorbesitzer Straße",
    previous_owner_house_number: "Vorbesitzer Hausnr.",
    previous_owner_postal_code: "Vorbesitzer PLZ",
    previous_owner_city: "Vorbesitzer Ort",
    mileage: "KM-Stand",
    last_service_date: "Letzter Service",
    has_service_book: "Scheckheft gepflegt",
    service_notes: "Service-Notizen",
};

const DOC_TYPE_LABEL = {
    zb1: "ZB1",
    zb2: "ZB2",
    coc: "COC",
    service_heft: "Service-Heft",
    werkstattauftrag: "Werkstattauftrag",
    handschriftlich: "Handnotiz",
    lieferschein: "Lieferschein",
    lieferanten_rechnung: "Lieferanten-Rechnung",
    unknown: "Unbekannt",
};

const DOC_TYPE_BADGE = {
    zb1: "bg-blue-100 text-blue-800 border-blue-200",
    zb2: "bg-indigo-100 text-indigo-800 border-indigo-200",
    coc: "bg-purple-100 text-purple-800 border-purple-200",
    service_heft: "bg-green-100 text-green-800 border-green-200",
    werkstattauftrag: "bg-amber-100 text-amber-800 border-amber-200",
    handschriftlich: "bg-orange-100 text-orange-800 border-orange-200",
    lieferschein: "bg-sky-100 text-sky-800 border-sky-200",
    lieferanten_rechnung: "bg-rose-100 text-rose-800 border-rose-200",
    unknown: "bg-gray-100 text-gray-700 border-gray-200",
};

// Jul-2026 (User-Wunsch) — Verlauf der zuletzt gescannten Papiere, damit
// nichts doppelt erfasst wird. Lädt aus GET /listings/scan-history.
function ScanHistoryBlock({ reload }) {
    const [days, setDays] = useState(30);
    const [items, setItems] = useState(null);
    // Jul-2026 (User-Wunsch) — Verlauf nach FIN / Dateiname / Kunde durchsuchen.
    const [q, setQ] = useState("");
    useEffect(() => {
        let alive = true;
        const t = setTimeout(() => {
            api.get("/listings/scan-history", { params: { days, q: q.trim() || undefined } })
                .then((r) => { if (alive) setItems(r.data?.items || []); })
                .catch(() => { if (alive) setItems([]); });
        }, q ? 300 : 0);
        return () => { alive = false; clearTimeout(t); };
    }, [days, reload, q]);
    if (items !== null && items.length === 0 && days >= 90 && !q) return null;
    return (
        <div className="mt-3 bg-white border border-gray-200 rounded-sm p-3" data-testid="zb1-scan-history">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 inline-flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5" /> Zuletzt gescannte Papiere — Doppel-Erfassung vermeiden
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="FIN, Dateiname, Kunde …"
                        className="h-6 w-44 border border-gray-300 rounded-sm px-1.5 text-[11px] bg-white focus:outline-none focus:border-[#FFCC00]"
                        data-testid="zb1-history-search"
                    />
                    {q && (
                        <button type="button" onClick={() => setQ("")}
                            className="text-gray-400 hover:text-gray-700" aria-label="Suche löschen"
                            data-testid="zb1-history-search-clear">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <div className="flex items-center gap-1">
                        {[7, 30, 90].map((d) => (
                            <button key={d} type="button" onClick={() => setDays(d)}
                                className={`text-[10px] font-bold rounded-sm px-1.5 py-0.5 border ${days === d ? "bg-[#111827] text-white border-[#111827]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                                data-testid={`zb1-history-days-${d}`}>
                                {d} Tage
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {items === null ? (
                <p className="text-xs text-gray-400 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Verlauf wird geladen…</p>
            ) : items.length === 0 ? (
                <p className="text-xs text-gray-400" data-testid="zb1-history-empty">
                    {q ? `Keine Treffer für „${q}" in den letzten ${days} Tagen.` : `Keine Scans in den letzten ${days} Tagen.`}
                </p>
            ) : (
                <ul className="divide-y divide-gray-100 max-h-44 overflow-auto">
                    {items.map((h, i) => (
                        <li key={h.id || i} className="py-1.5 flex items-center gap-2 text-[11px] flex-wrap" data-testid={`zb1-history-row-${i}`}>
                            <span className="text-gray-400 font-mono-num shrink-0 w-24">
                                {h.created_at ? new Date(h.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                            </span>
                            {h.doc_url ? (
                                <a href={h.doc_url} target="_blank" rel="noopener noreferrer"
                                    className="font-bold text-blue-700 underline truncate max-w-[180px]"
                                    data-testid={`zb1-history-link-${i}`}>{h.filename}</a>
                            ) : (
                                <span className="font-bold truncate max-w-[180px]">{h.filename}</span>
                            )}
                            <span className={`text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 border rounded-sm ${DOC_TYPE_BADGE[h.document_type] || DOC_TYPE_BADGE.unknown}`}>
                                {DOC_TYPE_LABEL[h.document_type] || h.document_type || "?"}
                            </span>
                            {h.vin && <span className="font-mono text-gray-600">FIN {h.vin}</span>}
                            {(h.manufacturer || h.model) && <span className="text-gray-500">{[h.manufacturer, h.model].filter(Boolean).join(" ")}</span>}
                            {h.customer_name && <span className="text-gray-500">· {h.customer_name}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function ZB1ScanCard({ onResult, bottomSlot, qrSlot }) {
    // items = [{file, preview, status: "pending"|"scanning"|"done"|"error", result?}]
    const [items, setItems] = useState([]);
    const [scanning, setScanning] = useState(false);
    // Jul-2026 (User-Wunsch) — Live-Fortschritt aus dem Hintergrund-Job:
    // pro Datei Seitenzahl, übersprungene Leerseiten + Erkennungs-Vorschau.
    const [liveProgress, setLiveProgress] = useState(null);
    const [historyReload, setHistoryReload] = useState(0);
    const [dragActive, setDragActive] = useState(false);
    const [warnings, setWarnings] = useState([]);
    // Aug-2026 (User-Wunsch): FIN nicht erkannt → rot animierte manuelle Nacheingabe
    const [manualVin, setManualVin] = useState("");
    const [manualVinApplied, setManualVinApplied] = useState(false);
    // Feb-2026 (User-Wunsch 24 May Iter 24): ZB2-Scan-Ergebnis öffnet sich
    // automatisch in einem separaten Modal, damit der Händler die Fahrzeug-
    // brief-Daten (Halter-Historie, Hubraum-Verifikation etc.) ungestört
    // prüfen kann, bevor sie ins Hauptformular einfließen.
    const [zb2DialogOpen, setZb2DialogOpen] = useState(false);
    const [zb2Doc, setZb2Doc] = useState(null);
    // Jul-2026 (User-Wunsch) — Scan-Button reagiert IMMER: ohne Dateien öffnet
    // er die Dateiauswahl, mit Dateien startet er den (erneuten) KI-Scan.
    const fileInputRef = useRef(null);

    useEffect(() => {
        const lastZb2 = [...items].reverse().find(
            (it) => it.status === "done" && (it.result?.document_type === "zb2"),
        );
        if (lastZb2 && (!zb2Doc || zb2Doc.file !== lastZb2.file)) {
            setZb2Doc(lastZb2);
            setZb2DialogOpen(true);
        }
    }, [items, zb2Doc]);

    const validateAndAdd = (rawFiles) => {
        const arr = Array.from(rawFiles || []);
        const valid = [];
        for (const f of arr) {
            if (!f) continue;
            if (f.size > MAX_BYTES) {
                toast.error(`Datei zu groß (max ${MAX_BYTES / 1024 / 1024} MB): ${f.name}`);
                continue;
            }
            const isAllowed = f.type.startsWith("image/") || f.type === "application/pdf";
            if (!isAllowed) {
                toast.error(`Nicht unterstützt: ${f.name} (nur JPG/PNG/PDF)`);
                continue;
            }
            valid.push(f);
        }
        if (!valid.length) return;
        setItems((prev) => {
            const combined = [...prev, ...valid.map((f) => ({
                file: f,
                preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
                status: "pending",
                result: null,
            }))];
            if (combined.length > MAX_FILES) {
                toast.error(`Maximal ${MAX_FILES} Dokumente pro Upload — überzählige Dateien ignoriert.`);
                return combined.slice(0, MAX_FILES);
            }
            return combined;
        });
        // Auto-Scan nach kurzer Verzögerung, damit setItems wirkt
        setTimeout(() => { scan(valid); }, 80);
    };

    const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
    const onDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
    const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
    const onDrop      = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        validateAndAdd(e.dataTransfer?.files);
    };

    const scan = async (filesToScan) => {
        const targets = filesToScan && filesToScan.length
            ? filesToScan
            : items.filter((it) => it.status !== "done").map((it) => it.file);
        if (!targets.length) {
            toast.error("Bitte zuerst Dokumente auswählen.");
            return;
        }
        setScanning(true);
        setManualVin("");
        setManualVinApplied(false);
        // Mark scanning state
        setItems((prev) => prev.map((it) => targets.includes(it.file) ? { ...it, status: "scanning" } : it));
        try {
            // Jul-2026 (Performance) — Fotos vor dem Upload clientseitig verkleinern
            // (5–10-MB-Handyfotos → ~400 KB): Upload + KI-Scan deutlich schneller.
            const uploadFiles = await Promise.all(targets.map((f) => compressForScan(f)));
            // Jul-2026 (502-Fix) — Verarbeitung als Hintergrund-Job mit Polling
            // statt langem HTTP-Request (Cloudflare bricht nach ~100 s ab).
            let respData;
            try {
                respData = await runDocumentsScanJob(uploadFiles, {
                    pollMs: 1500,
                    onStatus: (job) => setLiveProgress(job?.progress || null),
                });
            } catch (jobErr) {
                // Fallback: synchroner Endpoint (z. B. wenn Chunk-Upload blockiert wird)
                const fd = new FormData();
                uploadFiles.forEach((f) => fd.append("files", f));
                respData = (await api.post("/listings/scan-documents", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 120000,
                })).data;
            }
            const docs = respData?.documents || [];
            const warnings = respData?.warnings || [];
            // Update jedes item mit seinem Scan-Resultat (Reihenfolge stimmt mit Upload überein)
            setItems((prev) => {
                const next = [...prev];
                targets.forEach((f, idx) => {
                    const i = next.findIndex((it) => it.file === f);
                    if (i >= 0) {
                        next[i] = {
                            ...next[i],
                            status: docs[idx]?.mocked && docs[idx]?.reason ? "error" : "done",
                            result: docs[idx] || null,
                        };
                    }
                });
                return next;
            });

            // Auto-Merge + Apply: alle befüllten Felder + Dokument-URLs durchreichen
            const merged = respData?.merged_fields || {};
            const cleaned = { ...merged };
            // Dokument-Liste für CreateListingPage aufbereiten
            const documentsForForm = docs
                .filter((d) => d.document?.url)
                .map((d) => ({
                    url: d.document.url,
                    name: d.document.name,
                    kind: d.document.kind || d.document_type || "unknown",
                }));
            if (documentsForForm.length) {
                cleaned.__zb1_documents = documentsForForm;
                // Backwards-Compat: erstes Dokument auch als __zb1_document
                cleaned.__zb1_document = documentsForForm[0];
            }
            if (onResult) onResult(cleaned);

            const totalFields = Object.keys(merged).length;
            const okCount = docs.filter((d) => !d.mocked).length;
            const typesSeen = [...new Set(docs.map((d) => DOC_TYPE_LABEL[d.document_type] || d.document_type || "?"))].join(", ");
            setWarnings(warnings);
            if (warnings.length) {
                const critical = warnings.some((w) => w.severity === "critical");
                if (critical) {
                    toast.error(`${warnings.length} Diskrepanz(en) zwischen den Dokumenten — bitte prüfen!`);
                } else {
                    toast.warning(`${warnings.length} Abweichung(en) zwischen den Dokumenten erkannt.`);
                }
            }
            if (!okCount) {
                toast.message("Scan im Mock-Modus (keine KI-Erkennung verfügbar).");
            } else if (totalFields === 0) {
                toast.message("Keine Felder erkannt — bitte schärferes Foto verwenden.");
            } else {
                toast.success(`${totalFields} Felder aus ${docs.length} Dokument(en) erkannt — ${typesSeen}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Scan fehlgeschlagen");
            setItems((prev) => prev.map((it) => targets.includes(it.file) ? { ...it, status: "error" } : it));
        } finally {
            setScanning(false);
            setLiveProgress(null);
            setHistoryReload((n) => n + 1);
        }
    };

    const discardAll = () => {
        items.forEach((it) => { if (it.preview) URL.revokeObjectURL(it.preview); });
        setItems([]);
        setWarnings([]);
        setManualVin("");
        setManualVinApplied(false);
    };

    const removeItem = (file) => {
        setItems((prev) => prev.filter((it) => {
            if (it.file === file && it.preview) URL.revokeObjectURL(it.preview);
            return it.file !== file;
        }));
    };

    // Aggregierte erkannte Felder über alle Dokumente
    const aggregated = items
        .filter((it) => it.result?.fields)
        .reduce((acc, it) => {
            Object.entries(it.result.fields).forEach(([k, v]) => {
                if (["confidence", "document_type"].includes(k)) return;
                if (v == null || v === "") return;
                if (!(k in acc)) acc[k] = v;
            });
            return acc;
        }, {});

    const recognized = Object.entries(aggregated);
    const hasResults = items.some((it) => it.status === "done");
    // Aug-2026 (User-Wunsch): FIN fehlt nach dem Scan → auffällige Nacheingabe
    const vinMissing = hasResults && !scanning && !aggregated.vin && !manualVinApplied;
    const applyManualVin = () => {
        const v = manualVin.trim().toUpperCase().replace(/[\s-]/g, "");
        if (v.length < 5) { toast.error("Bitte eine gültige Fahrgestellnummer eingeben (mind. 5 Zeichen)"); return; }
        if (onResult) onResult({ vin: v });
        setManualVin(v);
        setManualVinApplied(true);
        toast.success("Fahrgestellnummer übernommen");
    };

    return (
        <div className="bg-twowheels-yellow/10 border border-twowheels-yellow/40 rounded-sm p-3" data-testid="zb1-scan-card">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                    <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                        <ScanLine className="w-4 h-4 text-twowheels-yellow-foreground shrink-0" />
                        Papiere scannen — COC · ZB1 · ZB2 · Serviceheft · Rechnungen
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                        KI erkennt Fahrzeugdaten & archiviert die Dokumente — bis zu {MAX_FILES} Dateien.
                    </div>
                </div>
            </div>

            {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v10) — Prominent
                sichtbare Scan-Lauf-Anzeige: animierte Progress-Bar + Zähler,
                damit Inserent klar erkennt, dass die KI arbeitet. Erscheint
                NUR während `scanning === true`. */}
            {scanning && (() => {
                const total = items.length || 0;
                const done = items.filter((it) => it.status === "done" || it.status === "error").length;
                const inFlight = items.filter((it) => it.status === "scanning").length;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                    <div
                        className="mb-4 bg-gradient-to-r from-[#FFCC00] to-[#FFA500] border-2 border-[#111827] rounded-sm p-4 shadow-lg"
                        data-testid="zb1-scan-running-banner"
                        role="status"
                        aria-live="polite"
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <Loader2 className="w-6 h-6 text-[#111827] animate-spin shrink-0" strokeWidth={2.5} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-black text-[#111827] uppercase tracking-wider">
                                    KI scannt deine Dokumente — bitte warten
                                </div>
                                <div className="text-xs text-[#111827]/80 mt-0.5 font-mono-num">
                                    {done} von {total} fertig
                                    {inFlight > 0 && ` · ${inFlight} laufen gerade`}
                                    {" · "}{pct} %
                                </div>
                            </div>
                        </div>
                        <div className="w-full h-2 bg-white/40 rounded-sm overflow-hidden">
                            <div
                                className="h-full bg-[#111827] transition-all duration-500"
                                style={{ width: `${Math.max(8, pct)}%` }}
                                data-testid="zb1-scan-running-progress"
                            />
                        </div>
                        {/* Jul-2026 (User-Wunsch) — Live: welche Seiten die KI liest
                            und was pro Dokument bereits erkannt wurde. */}
                        {Array.isArray(liveProgress) && liveProgress.length > 0 && (
                            <div className="mt-2.5 space-y-1" data-testid="zb1-live-progress">
                                {[...liveProgress].sort((a, b) => (a.index || 0) - (b.index || 0)).map((p) => (
                                    <div
                                        key={p.index}
                                        className="bg-white/75 rounded-sm px-2 py-1.5 text-[11px] text-[#111827] flex flex-wrap items-center gap-x-1.5 gap-y-1"
                                        data-testid={`zb1-live-file-${p.index}`}
                                    >
                                        <span className="font-bold truncate max-w-[180px]">{p.filename}</span>
                                        <span className="opacity-50">—</span>
                                        {p.stage === "done" ? (
                                            <>
                                                <span className="font-bold text-green-800 inline-flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3" />
                                                    {DOC_TYPE_LABEL[p.document_type] || p.document_type}
                                                </span>
                                                <span>· {p.fields_found} Felder erkannt</span>
                                                {p.preview && Object.entries(p.preview).map(([k, v]) => (
                                                    <span key={k} className="bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm font-mono text-[10px]">
                                                        {k}: {String(v)}
                                                    </span>
                                                ))}
                                            </>
                                        ) : p.stage === "ai" ? (
                                            <span className="inline-flex items-center gap-1">
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                KI liest {p.pages_total ? `${p.pages_total} Seite(n)` : "das Dokument"}
                                                {p.pages_skipped ? ` · ${p.pages_skipped} Leerseite(n) übersprungen` : ""} …
                                            </span>
                                        ) : p.stage === "reading" ? (
                                            <span>
                                                {p.pages_total} Seite(n) erkannt
                                                {p.pages_skipped ? ` · ${p.pages_skipped} Leerseite(n) übersprungen` : ""} — Übergabe an KI …
                                            </span>
                                        ) : (
                                            <span className="opacity-70">wird hochgeladen / wartet …</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="text-[10px] text-[#111827]/70 mt-1.5 italic">
                            Daten aus ZB1, COC, ZB2 und Serviceheft werden strukturiert extrahiert · {"<"} 10 s pro Dokument
                        </div>
                    </div>
                );
            })()}

            <div className="flex flex-col sm:flex-row gap-3 items-start">
                <label
                    className={`relative overflow-hidden flex-1 flex flex-col items-center justify-center gap-1.5 p-4 border-2 border-dashed rounded-sm cursor-pointer transition-colors min-h-[112px] ${dragActive ? "border-twowheels-yellow bg-twowheels-yellow/10" : "border-gray-300 bg-white hover:bg-gray-50"}`}
                    onDragEnter={onDragEnter}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    data-testid="zb1-upload-zone"
                >
                    {/* Muster-ZB1 als blasses Hintergrund-Wasserzeichen */}
                    <Zb1Watermark className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-[92%] w-auto opacity-[0.16]" />
                    <ImagePlus className="relative z-10 w-6 h-6 text-gray-500" />
                    <span className="relative z-10 text-xs font-bold uppercase tracking-wider">
                        {dragActive ? "Hier ablegen" : "Fotos / PDFs wählen oder hier reinziehen"}
                    </span>
                    <span className="relative z-10 text-[10px] text-gray-500">
                        Mehrere Dateien zulässig · ZB1 + COC + Service-Heft auf einmal
                    </span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        capture="environment"
                        onChange={(e) => { validateAndAdd(e.target.files); e.target.value = ""; }}
                        className="hidden"
                        data-testid="zb1-input"
                    />
                </label>

                <div className="flex flex-col gap-1.5 w-full sm:w-auto sm:min-w-[230px]">
                    <Button
                        type="button"
                        onClick={() => { if (!items.length) { fileInputRef.current?.click(); } else { scan(); } }}
                        disabled={scanning}
                        className={`h-9 font-semibold rounded-sm transition-all ${scanning ? "bg-[#111827] text-[#FFCC00] animate-pulse cursor-wait" : "bg-twowheels-yellow text-twowheels-yellow-foreground hover:brightness-95"}`}
                        data-testid="zb1-scan-btn"
                    >
                        {scanning
                            ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> KI scannt …</>
                            : items.length
                                ? <><Sparkles className="w-4 h-4 mr-1.5" /> Erneut scannen</>
                                : <><ScanLine className="w-4 h-4 mr-1.5" /> Scannen — Datei wählen</>}
                    </Button>
                    {/* Jul-2026 (User-Wunsch) — „Code für Handy scannen" DIREKT in den
                        Scan-Block integriert (statt als separate Box darüber). So stehen
                        Datei-Upload, Handy-QR-Scan und Sprache im selben Block. */}
                    {qrSlot && (
                        <div className="flex items-center gap-2" data-testid="zb1-qr-slot">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold shrink-0">oder am Handy</span>
                            <div className="flex-1">{qrSlot}</div>
                        </div>
                    )}
                    {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v3): KI-Sprachaufnahme
                        DIREKT unter dem „Erneut scannen"-Button platziert (statt als
                        full-width Block darunter), damit Scan-Button und Voice-Recorder
                        in derselben Spalte stehen. */}
                    {bottomSlot && (
                        <div data-testid="zb1-voice-slot">
                            {bottomSlot}
                        </div>
                    )}
                    {items.length > 0 && !scanning && (
                        <Button type="button" variant="ghost" size="sm" onClick={discardAll} className="text-xs text-gray-500" data-testid="zb1-discard">
                            <X className="w-3 h-3 mr-1" /> Alle verwerfen
                        </Button>
                    )}
                </div>
            </div>

            {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v3): Voice-Slot ist jetzt
                in der rechten Spalte direkt unter dem Scan-Button. Der frühere
                Full-Width-Slot wurde entfernt. */}

            {/* Pro-Datei Status-Liste */}
            {items.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="zb1-files-list">
                    {items.map((it, idx) => {
                        const dt = it.result?.document_type || "unknown";
                        return (
                            <div
                                key={`${it.file.name}-${idx}`}
                                className="flex items-center gap-2 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-xs"
                                data-testid={`zb1-file-row-${idx}`}
                            >
                                {it.preview ? (
                                    <img src={it.preview} alt="" className="w-8 h-8 object-cover rounded-sm shrink-0" />
                                ) : (
                                    <FileText className="w-5 h-5 text-gray-400 shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="truncate font-medium text-gray-800">{it.file.name}</div>
                                    <div className="text-[10px] text-gray-500">
                                        {(it.file.size / 1024).toFixed(0)} KB
                                    </div>
                                </div>
                                {it.status === "scanning" && (
                                    <span className="flex items-center gap-1 text-blue-700">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Scanne…
                                    </span>
                                )}
                                {it.status === "done" && (
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border rounded-sm ${DOC_TYPE_BADGE[dt] || DOC_TYPE_BADGE.unknown}`}>
                                        {DOC_TYPE_LABEL[dt] || dt}
                                    </span>
                                )}
                                {it.status === "error" && (
                                    <span className="flex items-center gap-1 text-red-700">
                                        <AlertCircle className="w-3 h-3" /> Fehler
                                    </span>
                                )}
                                {!scanning && (
                                    <button
                                        type="button"
                                        onClick={() => removeItem(it.file)}
                                        className="p-1 text-gray-400 hover:text-red-600"
                                        data-testid={`zb1-file-remove-${idx}`}
                                        aria-label="Entfernen"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Cross-Check-Warnungen zwischen Dokumenten */}
            {warnings.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="zb1-cross-check-warnings">
                    {warnings.map((w, idx) => {
                        const isCritical = w.severity === "critical";
                        return (
                            <div
                                key={`warn-${idx}`}
                                className={`flex gap-2 p-2 border rounded-sm text-xs ${isCritical ? "bg-red-50 border-red-300 text-red-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}
                                data-testid={`zb1-warning-${idx}`}
                            >
                                <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${isCritical ? "text-red-600" : "text-amber-600"}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold">
                                        {isCritical ? "Kritisch" : "Hinweis"}: {w.label} weicht ab
                                    </div>
                                    <div className="mt-0.5 space-y-0.5">
                                        {(w.values || []).map((v, i) => (
                                            <div key={i} className="font-mono text-[11px]">
                                                <span className="uppercase font-bold mr-1">{DOC_TYPE_LABEL[v.document_type] || v.document_type}:</span>
                                                <span>{String(v.value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="text-[10px] mt-1 opacity-80">{w.message}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Aggregierte erkannte Felder */}
            {hasResults && recognized.length > 0 && (
                <div className="mt-3 bg-white border border-gray-200 rounded-sm p-3" data-testid="zb1-extracted-fields">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                            Erkannte Felder ({recognized.length}) — auto-gemerged aus {items.filter((i) => i.status === "done").length} Dokument(en)
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <ul className="text-xs text-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 max-h-56 overflow-auto">
                        {recognized.map(([k, v]) => (
                            <li key={k} className="flex gap-2">
                                <span className="font-mono text-gray-500 w-36 shrink-0 truncate">{FIELD_LABELS[k] || k}:</span>
                                <span className="font-medium truncate">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="text-[10px] text-gray-500 mt-2">
                        Felder wurden automatisch ins Formular übernommen — bitte vor Veröffentlichung prüfen.
                    </div>
                </div>
            )}

            {/* Aug-2026 (User-Wunsch): FIN nicht erkannt → rot markierte, pulsierende Nacheingabe */}
            {vinMissing && (
                <div className="mt-3 bg-red-50 border-2 border-red-500 rounded-sm p-3 animate-pulse" data-testid="zb1-vin-missing">
                    <div className="flex items-center gap-2 mb-1.5">
                        <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                        <div className="text-xs font-bold text-red-700 uppercase tracking-wider">Fahrgestellnummer (FIN) nicht erkannt — bitte eingeben</div>
                    </div>
                    <div className="flex gap-2">
                        <input
                            value={manualVin}
                            onChange={(e) => setManualVin(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && applyManualVin()}
                            placeholder="FIN eintippen…"
                            className="flex-1 border border-red-300 bg-white rounded-sm px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-red-500"
                            autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                            data-testid="zb1-vin-manual-input"
                        />
                        <Button onClick={applyManualVin} size="sm"
                            className="rounded-sm bg-red-600 hover:bg-red-700 text-white font-bold shrink-0"
                            data-testid="zb1-vin-manual-apply">
                            Übernehmen
                        </Button>
                    </div>
                </div>
            )}
            {manualVinApplied && (
                <div className="mt-3 bg-emerald-50 border border-emerald-300 rounded-sm px-3 py-2 text-xs text-emerald-800 inline-flex items-center gap-1.5 w-full" data-testid="zb1-vin-manual-done">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> Fahrgestellnummer <b className="font-mono">{manualVin}</b> manuell übernommen.
                </div>
            )}

            {/* Jul-2026 (User-Wunsch) — Verlauf der zuletzt gescannten Papiere */}
            <ScanHistoryBlock reload={historyReload} />

            {/* Feb-2026 (User-Wunsch 24 May Iter 24): ZB2 (Fahrzeugbrief)
                Scan-Ergebnis in eigenem Popup — gibt dem Händler einen
                ungestörten Review-Schritt für die Halter-Historie, Schlüssel
                und Identitätsmerkmale, BEVOR die Daten ins Hauptformular
                übernommen werden. */}
            <Dialog open={zb2DialogOpen} onOpenChange={setZb2DialogOpen}>
                <DialogContent className="max-w-2xl" data-testid="zb2-result-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSignature className="w-5 h-5 text-indigo-600" />
                            ZB2 (Fahrzeugbrief) — KI-Scan-Ergebnis
                        </DialogTitle>
                        <DialogDescription>
                            Prüfe die Halter-Historie und Identitätsmerkmale, bevor sie ins Formular übernommen werden.
                        </DialogDescription>
                    </DialogHeader>
                    {zb2Doc?.result?.fields ? (
                        <div className="max-h-[60vh] overflow-auto">
                            <ul className="text-sm text-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="zb2-result-fields">
                                {Object.entries(zb2Doc.result.fields)
                                    .filter(([k, v]) => v != null && v !== "" && !["confidence", "document_type"].includes(k))
                                    .map(([k, v]) => (
                                        <li key={k} className="flex gap-2 py-1 border-b border-gray-100 last:border-0">
                                            <span className="font-mono text-gray-500 w-44 shrink-0 truncate text-xs">
                                                {FIELD_LABELS[k] || k}:
                                            </span>
                                            <span className="font-medium truncate">
                                                {Array.isArray(v) ? v.join(", ") : String(v)}
                                            </span>
                                        </li>
                                    ))}
                            </ul>
                            {zb2Doc.result.fields.confidence != null && (
                                <div className="mt-3 text-[11px] text-gray-500 italic">
                                    KI-Konfidenz: {(zb2Doc.result.fields.confidence * 100).toFixed(0)} %
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-sm text-gray-500">Keine ZB2-Daten erkannt.</div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setZb2DialogOpen(false)}
                            data-testid="zb2-dialog-close"
                        >
                            Schließen
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
